import {keyframes} from '@mui/system'
import {Box, TextField, Tooltip, Typography, Paper, Stack, Button} from '@mui/material'
import {ClipLoader} from 'react-spinners'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import axios from 'axios'
import {useState} from 'react'

// Define keyframe animations
const sparkle = keyframes`
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
  25% {
    transform: scale(1.1) rotate(90deg);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.2) rotate(180deg);
    opacity: 0.6;
  }
  75% {
    transform: scale(1.1) rotate(270deg);
    opacity: 0.8;
  }
`

const blink = keyframes`
  0%, 50%, 100% {
    opacity: 1;
  }
  25%, 75% {
    opacity: 0.3;
  }
`

const glow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 5px #007bff) drop-shadow(0 0 10px #007bff);
  }
  50% {
    filter: drop-shadow(0 0 10px #007bff) drop-shadow(0 0 20px #007bff) drop-shadow(0 0 30px #007bff);
  }
`

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`

const SmartTextarea = ({formData,setFormData, field, disabled}: any) => {
  const [text, setText] = useState(formData[field.name] || '')
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [apiResponse, setApiResponse] = useState('')
  const [originalText, setOriginalText] = useState('')

  // API call function - replace with your actual API endpoint
  const callAPI = async (payload: string) => {
    setIsLoading(true)
    try {
      const result = await axios.post('/clients/getBedrockResponse', {
        prompt: payload,
      })
      const {data} = result.data
      const updatedResponse = JSON.parse(data?.body)
      setApiResponse(updatedResponse?.rewritten_text)
      setShowModal(true)
    } catch (error) {
      console.error('API Error:', error)
      setApiResponse('Error: Failed to process your request. Please try again.')
      setShowModal(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleIconClick = () => {
    if (text.trim()) {
      setOriginalText(text)
      callAPI(text)
    }
  }

  const handleRefetch = () => {
    setShowModal(false)
    callAPI(originalText)
  }

  const handleApply = () => {
    setText(apiResponse)  
    setFormData({...formData, [field.name]: apiResponse})
    setShowModal(false)
    setApiResponse('')
    setOriginalText('')
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setApiResponse('')
    setOriginalText('')
  }

  return (
    <Box>
      {/* Text Field Container */}
      <Box sx={{position: 'relative'}}>
        <TextField
          value={text}
          onChange={(e) => {
            setText(e.target.value)
          }}
          placeholder='Type your text here...'
          multiline
          rows={4}
          fullWidth
          variant='outlined'
          sx={{
            '& .MuiOutlinedInput-root': {
              paddingRight: '50px', // Make space for the icon
              '& fieldset': {
                borderColor: '#ddd',
              },
              '&:hover fieldset': {
                borderColor: '#007bff',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#007bff',
              },
            },
            '& .MuiInputBase-input': {
              fontSize: '14px',
              fontFamily: 'inherit',
            },
          }}
        />

        {/* Icon/Loader Container with Tooltip */}
        <Tooltip
          open={showModal}
          onClose={() => {}} // Prevent closing on mouse leave
          disableHoverListener
          disableFocusListener
          disableTouchListener
          title={
            <Box sx={{maxWidth: '400px', p: 1}}>
              <Typography variant='subtitle2' color='text.secondary' sx={{mb: 1, fontWeight: 600}}>
                AI Enhanced Description:
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.200',
                  fontSize: '12px',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  mb: 2,
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                {apiResponse}
              </Paper>
              <Stack direction='row' spacing={1} justifyContent='flex-end'>
                <Button
                  onClick={handleCloseModal}
                  variant='outlined'
                  color='secondary'
                  size='small'
                  sx={{fontSize: '11px', minWidth: '60px'}}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRefetch}
                  variant='outlined'
                  color='primary'
                  size='small'
                  sx={{fontSize: '11px', minWidth: '60px'}}
                >
                  Re-Generate
                </Button>
                <Button
                  onClick={handleApply}
                  variant='contained'
                  color='primary'
                  size='small'
                  sx={{fontSize: '11px', minWidth: '60px'}}
                >
                  Apply
                </Button>
              </Stack>
            </Box>
          }
          placement='top-end'
          arrow
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: 'white',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 3,
                maxWidth: 'none',
                p: 0,
              },
              '& .MuiTooltip-arrow': {
                color: 'white',
                '&::before': {
                  border: '1px solid',
                  borderColor: 'divider',
                },
              },
            },
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 8], // Adjust horizontal and vertical offset
                },
              },
            ],
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: text.trim() ? 'pointer' : 'not-allowed',
              opacity: text.trim() ? 1 : 0.5,
              transition: 'opacity 0.3s ease',
              animation: text.trim() ? `${pulse} 3s ease-in-out infinite` : 'none',
              '&:hover': {
                animation: text.trim() ? `${pulse} 3s ease-in-out infinite` : 'none',
              },
            }}
            onClick={handleIconClick}
          >
            {isLoading ? (
              <ClipLoader size={16} color='#007bff' />
            ) : (
              <AutoAwesomeIcon
                sx={{
                  color: text.trim() ? '#007bff' : '#ccc',
                  fontSize: '20px',
                  animation: text.trim()
                    ? `${sparkle} 3s ease-in-out infinite, ${blink} 2s ease-in-out infinite, ${glow} 4s ease-in-out infinite`
                    : 'none',
                  '&:hover': {
                    animation: text.trim()
                      ? `${sparkle} 3s ease-in-out infinite, ${blink} 2s ease-in-out infinite, ${glow} 4s ease-in-out infinite`
                      : 'none',
                  },
                }}
              />
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  )
}
export default SmartTextarea
